import { Fragment } from "react";
import { ShieldAlertIcon } from "lucide-react";
import type { IpFlagKind, PlayerModalPlayerData } from "@shared/playerApiTypes";
import { ModalTabInner } from "@/components/modal-tabs";
import { tsToLocaleDateTimeString } from "@/lib/dateTime";


//kept in sync with the ingame tab, both read from the same payload
export const ipFlagLabels: Record<IpFlagKind, string> = {
    vpn: 'VPN',
    proxy: 'Proxy',
    tor: 'Tor Exit Node',
    datacenter: 'Datacenter',
    crawler: 'Crawler',
    abuser: 'Known Abuser',
};

const scoreLabel = (score: number) => {
    const pct = `${(score * 100).toFixed(2)}%`;
    if (score >= 0.1) return `${pct} (high)`;
    if (score >= 0.01) return `${pct} (elevated)`;
    return `${pct} (low)`;
};


type PlayerFlagsTabProps = {
    player: PlayerModalPlayerData;
}

export default function PlayerFlagsTab({ player }: PlayerFlagsTabProps) {
    const ipFlags = player.ipFlags;
    if (!ipFlags) {
        return <ModalTabInner>
            <span className="text-muted-foreground italic">
                This player's address was never checked.
            </span>
        </ModalTabInner>;
    }

    const details: [string, string][] = [];
    if (typeof ipFlags.abuserScore === 'number') {
        details.push(['Abuser Score', scoreLabel(ipFlags.abuserScore)]);
    }
    if (ipFlags.asn) details.push(['ASN', `AS${ipFlags.asn}`]);
    if (ipFlags.country) details.push(['Country', ipFlags.country]);
    if (ipFlags.company) details.push(['Network', ipFlags.company]);
    details.push(['Checked', tsToLocaleDateTimeString(ipFlags.ts, 'medium', 'short')]);
    const sortedFlags = [...ipFlags.flags].sort();

    return (
        <ModalTabInner className="flex flex-col gap-4">
            <div className="flex items-center gap-2 text-warning">
                <ShieldAlertIcon className="size-5 shrink-0" />
                <h3 className="font-semibold">
                    {ipFlags.flags.length ? 'Suspicious connection' : 'Nothing flagged'}
                </h3>
            </div>

            {sortedFlags.length ? (
                <div className="flex flex-wrap gap-2">
                    {sortedFlags.map((flag) => (
                        <span
                            key={flag}
                            className="rounded-bar border border-warning/45 bg-warning/10 px-2 py-1 text-xs font-semibold text-warning"
                        >
                            {ipFlagLabels[flag] ?? flag}
                        </span>
                    ))}
                </div>
            ) : null}

            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                {details.map(([label, value]) => (
                    <Fragment key={label}>
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd className="break-all">{value}</dd>
                    </Fragment>
                ))}
            </dl>

            <p className="text-xs text-muted-foreground">
                Reputation of the address the player connected from.
                The address itself is never stored or shown.
            </p>
        </ModalTabInner>
    );
}
